/**
 * Paying the crews — docs/BUSINESS-RULES.md.
 *
 * Work orders completed by midnight Wednesday go to Lysa for review, and the
 * approved ones are paid by ACH on Thursday afternoon. She can approve, reject
 * with a note, or change the amount first — a crew that did not finish
 * everything gets paid for what it did.
 *
 * Approval is the gate to QuickBooks: nothing becomes a bill until a person has
 * said yes. Our own employees never appear here at all — they are on payroll,
 * not a bill.
 *
 * A payout can also have been paid already, by hand out of Mercury, before it
 * was ever reviewed. That is approval plus a note that the money has gone, so
 * it leaves the run without leaving the accounts — ADR 0172.
 *
 * A payout can also be put on hold: the crew has not sent a W-9, there is no
 * bank account to send to, or the order was a placeholder. It is parked with a
 * reason, out of the run and out of Thursday's email, until somebody approves
 * it or puts it back — ADR 0315.
 *
 * The approved list is the list of crews to pay, so it has to empty as they are
 * paid. A person says when: the money leaving the bank is a fact about Mercury,
 * and an ACH batch is one line there for several crews. Paid work stays on the
 * board briefly and then drops off it — ADR 0174.
 */

import { documentTotal } from "./ladder.js";
import { addCents, toCents, type Cents } from "./money.js";
import type { CostDocument, WorkOrder } from "./types.js";

/** The day the run is paid. 4 = Thursday, counting from Sunday. */
const PAY_DAY = 4;

/** How long a paid payout stays on the board before dropping off it. */
export const RECENTLY_PAID_DAYS = 14;

export type PayoutState =
  /** The crew has not finished, so there is nothing to pay yet. */
  | "not_ready"
  | "needs_review"
  | "approved"
  /** The money has left the bank. */
  | "paid"
  | "rejected"
  /** Parked with a reason, waiting on paperwork rather than a decision. */
  | "held"
  /** Billed to QuickBooks. Settled, and no longer editable. */
  | "sent";

export type PayoutOrder = Pick<
  WorkOrder,
  | "completed_at"
  | "payout_approved_at"
  | "payout_rejected_at"
  | "payout_held_at"
  | "payout_sent_at"
  | "payout_paid_at"
  | "payout_paid_manually_at"
  | "payout_amount"
> &
  CostDocument & {
    crew?: {
      name?: string | null;
      contact_name?: string | null;
      is_in_house?: boolean;
    } | null;
  };

/**
 * The cutoff for the run being worked on now.
 *
 * Midnight Wednesday is the start of Thursday. On a Thursday that is this
 * morning — the run is happening today. On any other day it is the Thursday
 * coming, so work finished on Monday is already in this week's run rather than
 * waiting eight days.
 */
export function reviewCutoff(now: Date = new Date()): Date {
  const cutoff = new Date(now);
  cutoff.setHours(0, 0, 0, 0);
  if (now.getDay() !== PAY_DAY) {
    const ahead = (PAY_DAY - now.getDay() + 7) % 7 || 7;
    cutoff.setDate(cutoff.getDate() + ahead);
  }
  return cutoff;
}

/** Employees are on payroll. Their work is costed, never billed. */
export const isPayable = (order: Pick<PayoutOrder, "crew">): boolean =>
  order.crew?.is_in_house !== true;

export function payoutState(
  order: Pick<
    PayoutOrder,
    | "completed_at"
    | "payout_approved_at"
    | "payout_rejected_at"
    | "payout_held_at"
    | "payout_sent_at"
    | "payout_paid_at"
  >,
): PayoutState {
  if (order.payout_sent_at) return "sent";
  if (order.payout_rejected_at) return "rejected";
  // Paid is read before approved because it is the more finished of the two:
  // a paid payout is always an approved one as well, and what somebody running
  // the Thursday run needs to know is that this one is no longer theirs to
  // send. QuickBooks is unaffected — it watches the approval.
  if (order.payout_paid_at) return "paid";
  // A hold is the same kind of answer as a rejection — not approved, not paid
  // — and the database clears it when either is written. It is read after
  // paid so that a row some writer left both paid and held is never shown as
  // still owed: money that has left the bank is the stronger fact (ADR 0315).
  if (order.payout_held_at) return "held";
  if (order.payout_approved_at) return "approved";
  return order.completed_at ? "needs_review" : "not_ready";
}

/**
 * What this work order pays.
 *
 * The approved amount when somebody changed it, otherwise the order's own
 * total. Never both — a partial approval replaces the figure, it does not
 * adjust it.
 */
export function payoutTotal(order: PayoutOrder): Cents {
  return order.payout_amount == null
    ? documentTotal(order)
    : toCents(order.payout_amount);
}

/** A payout as the work order shows it: where it is, for how much, who, when. */
export interface PayoutSummary {
  state: PayoutState;
  /** What it pays — `payoutTotal`. */
  amount: Cents;
  /** Who moved it to this state, as a profile id. Null before anybody has. */
  by: string | null;
  /** When. Null before anybody has. */
  at: string | null;
  /** Paid by hand before it was reviewed (ADR 0172). */
  offProcess: boolean;
}

/**
 * The payout, for the read-only line on the work order (ADR 0180).
 *
 * The payout board drops paid work after fourteen days and says the job keeps
 * the record; this is the record. The who and when are those of the state the
 * payout is in — the person who ticked it paid, approved it or sent it back.
 */
export function payoutSummary(
  order: PayoutOrder &
    Partial<
      Pick<
        WorkOrder,
        | "payout_approved_by"
        | "payout_paid_by"
        | "payout_rejected_by"
        | "payout_held_by"
      >
    >,
): PayoutSummary {
  const state = payoutState(order);
  const [by, at] =
    state === "paid"
      ? [order.payout_paid_by, order.payout_paid_at]
      : state === "rejected"
        ? [order.payout_rejected_by, order.payout_rejected_at]
        : state === "held"
          ? [order.payout_held_by, order.payout_held_at]
          : state === "approved" || state === "sent"
            ? [order.payout_approved_by, order.payout_approved_at]
            : [null, null];
  return {
    state,
    amount: payoutTotal(order),
    by: by ?? null,
    at: at ?? null,
    offProcess: state === "paid" && order.payout_paid_manually_at != null,
  };
}

export interface PayoutRun {
  /** Finished before the cutoff. This is the run. */
  due: PayoutOrder[];
  /** Finished after it. Lysa can still pull these in — she has the call. */
  early: PayoutOrder[];
  /** Approved and waiting for the money to go out. This is the run. */
  approved: PayoutOrder[];
  /** Paid recently. Older ones drop off the board — see RECENTLY_PAID_DAYS. */
  paid: PayoutOrder[];
  /** Sent back to the crew, with a note. */
  rejected: PayoutOrder[];
  /** On hold, with a reason: waiting on paperwork or a decision (ADR 0315). */
  held: PayoutOrder[];
  /** What the approved work adds up to. */
  approvedTotal: Cents;
}

/**
 * Sort the week's work orders into what Lysa is looking at.
 *
 * In-house crews are dropped before anything else: their work should never
 * reach a payout screen, so it is filtered here rather than hidden in the UI.
 */
export function payoutRun(
  orders: readonly PayoutOrder[],
  now: Date = new Date(),
): PayoutRun {
  const cutoff = reviewCutoff(now).getTime();
  const payable = orders.filter(isPayable);

  const inState = (state: PayoutState) =>
    payable.filter((o) => payoutState(o) === state);

  const review = inState("needs_review");
  const before = (o: PayoutOrder) =>
    o.completed_at != null && new Date(o.completed_at).getTime() < cutoff;

  const approved = inState("approved");

  // Paid work is shown for a fortnight and then gone. Long enough to answer
  // "did that go out?", short enough that the board stays a list of things to
  // do rather than a ledger — the job's own screen is where a payout is looked
  // up months later.
  const recent = now.getTime() - RECENTLY_PAID_DAYS * 86_400_000;

  return {
    due: review.filter(before),
    early: review.filter((o) => !before(o)),
    approved,
    // Money that has already left is not money waiting to go out, so it is
    // neither in the approved list nor in its total.
    paid: inState("paid").filter(
      (o) =>
        o.payout_paid_at != null &&
        new Date(o.payout_paid_at).getTime() >= recent,
    ),
    rejected: inState("rejected"),
    held: inState("held"),
    approvedTotal: addCents(...approved.map(payoutTotal)),
  };
}
